import type { CreateSavedDestinationInput } from "@solomon-apps/shared";
import {
    useOrgAuthStore,
    WalletVerificationMethod,
    YaasPositionStatus
} from "@solomon-apps/shared";
import { useWallet, useWalletModal } from "@solomon-apps/shared";
import {
    AccountOwnershipVerificationPanel,
    type VerificationCopy
} from "@solomon-apps/shared/src/components/console";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import {
    Alert,
    AlertDescription,
    AlertTitle
} from "@solomon-apps/shared/src/components/ui/alert";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { ConfirmModal } from "@solomon-apps/shared/src/components/ui/confirm-modal";
import { FormActions } from "@solomon-apps/shared/src/components/ui/form-actions";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { Button } from "@solomon-labs/design/components/ui";
import {
    Address,
    TabsMenu,
    TabTrigger
} from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ManualVerificationPending } from "@/components/ManualVerificationPending";
import {
    useClientPositions,
    useEditPositionDestinations,
    useResubmitPosition
} from "@/hooks/useClientPositions";
import { useCreateSavedDestination } from "@/hooks/useClientSavedDestinations";
import { isVerifyMember, useSquadsMultisig } from "@/hooks/useSquadsMultisig";
import { useViewOnly } from "@/hooks/useViewOnly";
import { issueRewardsDestinationChallenge } from "@/services/clientRewardsDestination";
import { OrgApiError } from "@/services/orgApiFetch";
import { useVerifyActionFlow } from "@/verification";

import { positionSubmitErrorCopy } from "../AddPosition/errorCopy";
import {
    findHolding,
    holdingDestinationAddresses
} from "../Positions/holdings";
import { useHoldings } from "../Positions/useHoldings";
import { truncateAddress } from "../shared/format";
import type { RewardsDestinationChoice } from "../shared/RewardsDestinationPicker";

import { ActionsRow, type EditPositionAction } from "./components/ActionsRow";
import { PositionKeyStats } from "./components/PositionKeyStats";
import { StatusBlock } from "./components/PositionSummaryCard";
import { PositionTransactionsCard } from "./components/PositionTransactionsCard";
import { RewardsDestinationSection } from "./components/RewardsDestinationSection";
/* ── Per-action verification copy ────────────────────────── */

const EDIT_REWARDS_COPY: VerificationCopy = {
    eyebrow: "Confirm rewards destination change",
    walletSignTitle: "Sign to set the new rewards destination.",
    walletSignBody:
        "This binds the new destination to your signature so it can't be replayed on another action.",
    committingTitle: "Updating destination.",
    committingBody:
        "Attaching the verification proof and writing the new destination.",
    successTitle: "Destination updated.",
    successSummary: "New rewards will stream to the new address.",
    successFootnote: "Past rewards are unaffected."
};

/* ── State machine ───────────────────────────────────────── */

/** The position page's tabs; Transactions opens first. */
type PositionDetailTab = "transactions" | "rewards";

interface PendingEditRewards {
    choice: RewardsDestinationChoice;
}

type PendingMutation = {
    kind: "edit-rewards";
    payload: PendingEditRewards;
} | null;

type ResubmitFeedback = null | {
    kind: "success" | "error";
    title: string;
    description: string;
};

/* ── Address-book save ───────────────────────────────────── */

/**
 * The address-book entry to persist when the user ticked "Save this for
 * later" on the rewards picker, or null when they didn't. Mirrors Add
 * Position: the new rewards address is written to Settings → Saved
 * destinations, labelled by the chosen label or the address itself.
 */
export function addressBookEntryFromChoice(
    choice: RewardsDestinationChoice
): CreateSavedDestinationInput | null {
    if (!choice.isSavedNew) return null;
    return { name: choice.label ?? choice.address, address: choice.address };
}

/* ── Props ───────────────────────────────────────────────── */

interface InlinePositionEditorProps {
    positionUuid: string;
    view?: "detail" | "routing";
    onDestinationSaved?: () => void;
    onRoutingCancel?: () => void;
    /** Optional back-link (e.g. "← Back to Positions") rendered tight above
     *  the header, inside the same gap-1 block. The standalone route passes
     *  it; inline usages (Dashboard) omit it. */
    backLink?: React.ReactNode;
}

/* ── Component ───────────────────────────────────────────── */

export function InlinePositionEditor({
    positionUuid,
    backLink,
    view = "detail",
    onDestinationSaved,
    onRoutingCancel
}: InlinePositionEditorProps): React.ReactElement {
    const {
        positions,
        isLoading,
        error: positionsError
    } = useClientPositions();
    const viewOnly = useViewOnly();
    const issuerName = useOrgAuthStore(
        (state) => state.me?.issuerOrg?.name ?? "Solomon"
    );

    /* Rewards-destination variant — `single` (one wallet address) for now so
     * every position shows the simpler model; `?rewards=multiple` previews the
     * split-across-saved-destinations model. PREVIEW override until the variant
     * is driven by the position/pool config. */
    const [searchParams] = useSearchParams();
    const rewardsVariant =
        searchParams.get("rewards") === "multiple" ? "multiple" : "single";
    const { publicKey, connected, isWalletReady, disconnect } = useWallet();
    const { openModal } = useWalletModal();
    const editDestination = useEditPositionDestinations();
    const createDestination = useCreateSavedDestination();
    const resubmit = useResubmitPosition();

    /* The route keeps `/positions/:positionUuid` for link stability; the page
     * renders the whole holding containing that position (every Program
     * Position on the same wallet / LP position, merged — Programs are never
     * shown). `position` is the record whose status the holding shows; the
     * wallet-level fields it supplies (account, source kind) are the same on
     * every underlying record. */
    const holdings = useHoldings(positions);
    const holding = findHolding(holdings, positionUuid);
    const position = holding?.statusSource ?? null;
    const awaitingManualReview =
        holding?.status === YaasPositionStatus.Pending &&
        holding.positions.some(
            (p) =>
                p.status === YaasPositionStatus.Pending &&
                p.verificationMode === "direct-solomon"
        );
    /* A failed fetch must not masquerade as "not found" — that claims the
     * position doesn't exist when we simply couldn't load the list. */
    const loadFailed = !isLoading && !position && Boolean(positionsError);
    const notFound = !isLoading && !position && !positionsError;
    let positionTitle = loadFailed
        ? "Couldn't load position"
        : "Position not found";
    if (holding) positionTitle = holding.name;
    // The distinct LP positions behind a pool holding (one per NFT, however
    // many Programs it sits under).
    const lpPositionAddresses = [
        ...new Set(
            (holding?.positions ?? []).flatMap((row) =>
                row.lpPosition
                    ? [
                          row.lpPosition.canonicalPositionAddress ??
                              row.lpPosition.positionAddress
                      ]
                    : []
            )
        )
    ];

    const walletAddress = publicKey?.toBase58() ?? null;

    const isMultisigPosition = position?.sourceKind === "multisig-squads";
    const multisigStatus = useSquadsMultisig(
        isMultisigPosition ? (position?.account.address ?? "") : "",
        walletAddress
    );

    /* For EOA positions the connected wallet must be the source account
     * itself — the verification message is signed by `walletAddress` and
     * the server checks the signature against the position's source.
     * For multisig positions any connected wallet can render the panel;
     * the Vote-permission gate runs at Save time below. */
    const eoaWalletMismatch =
        position !== null &&
        !isMultisigPosition &&
        walletAddress !== null &&
        walletAddress !== position.account.address;
    const needsSourceWallet =
        position !== null && (walletAddress === null || eoaWalletMismatch);

    /* ── Sub-flow state ──────────────────────────────────── */

    const [detailTab, setDetailTab] =
        useState<PositionDetailTab>("transactions");
    const [activeAction, setActiveAction] = useState<EditPositionAction | null>(
        null
    );
    const [pendingMutation, setPendingMutation] =
        useState<PendingMutation>(null);
    const [resubmitFeedback, setResubmitFeedback] =
        useState<ResubmitFeedback>(null);
    const [showResubmitConfirm, setShowResubmitConfirm] =
        useState<boolean>(false);
    const [multisigGateError, setMultisigGateError] = useState<string | null>(
        null
    );

    // Reset sub-flow state whenever the selected position changes
    useEffect(() => {
        setActiveAction(null);
        setPendingMutation(null);
        setResubmitFeedback(null);
        setShowResubmitConfirm(false);
        setMultisigGateError(null);
    }, [positionUuid]);

    const verification = useVerifyActionFlow();

    /* ── Action launchers ────────────────────────────────── */

    const handleAction = useCallback((action: EditPositionAction) => {
        setActiveAction(action);
        if (action === "resubmit") {
            setShowResubmitConfirm(true);
            return;
        }
        // edit-rewards — inline panel opens, no confirm needed
    }, []);

    // One challenge per edit; a challenge for an abandoned edit is dropped.
    // The proof is bound to the signer and client context server-side.
    const activeEdit = useRef(pendingMutation);
    activeEdit.current = pendingMutation;
    useEffect(
        () => (): void => {
            activeEdit.current = null;
        },
        []
    );
    const challengeRequest = useRef<typeof pendingMutation>(null);

    /* ── Kick off verification when a mutation is pending ── */

    useEffect(() => {
        if (!pendingMutation) {
            challengeRequest.current = null;
            return;
        }
        if (challengeRequest.current === pendingMutation) return;
        if (!walletAddress || needsSourceWallet) return;
        if (verification.step !== null) return;
        /* Multisig positions: gate verification on Vote-permission
         * membership of the connected wallet. Re-checked at submit
         * time on the server. */
        if (isMultisigPosition) {
            if (multisigStatus.status === "lookup-failed") {
                setMultisigGateError(
                    "Couldn't reach the multisig right now. Try again."
                );
                setPendingMutation(null);
                return;
            }
            if (multisigStatus.status !== "success") {
                setMultisigGateError(
                    "Still checking current multisig membership. Try again in a moment."
                );
                setPendingMutation(null);
                return;
            }
            if (!isVerifyMember(multisigStatus)) {
                setMultisigGateError(
                    "This wallet can't currently verify the multisig. Connect a member wallet with Vote or Execute permission to edit."
                );
                setPendingMutation(null);
                return;
            }
        }
        if (!position || !holding) return;
        challengeRequest.current = pendingMutation;

        const { choice } = pendingMutation.payload;
        const primaryUuid = position.uuid;
        const selection = {
            programPositionUuids: [...holding.positionUuids].sort(),
            setWalletDefault: holding.positions.every((row) => !row.lpPosition),
            rewardsDestinationAddress: choice.address,
            ...(isMultisigPosition ? { signerAddress: walletAddress } : {})
        };
        const signerAddress =
            isMultisigPosition && walletAddress ? walletAddress : null;

        void (async (): Promise<void> => {
            try {
                const challenge = await issueRewardsDestinationChallenge(
                    primaryUuid,
                    selection
                );
                if (activeEdit.current !== pendingMutation) return;
                verification.startFlow({
                    address: walletAddress,
                    messageToSign: challenge.message,
                    microTransferMemo: challenge.microTransferMemo,
                    method: "signMessage",
                    /* The mutation rides along as the flow's `commit` step: it runs
                     * exactly once, while the panel shows "committing", and the flow
                     * only advances to "success" after the PUT lands — so the
                     * success copy is truthful and a slow network can't re-trigger a
                     * duplicate write. (The previous 600ms timer-effect commit
                     * double-fired when the PATCH outlived its re-arm window.) On
                     * failure we clear the pending mutation and surface mapped copy;
                     * throwing lets the flow reset to idle, re-revealing the editor
                     * with the error Alert. */
                    commit: async (proof) => {
                        try {
                            await editDestination.mutateAsync({
                                positionUuid: primaryUuid,
                                data: {
                                    ...selection,
                                    verificationMethod:
                                        proof.method === "signMessage"
                                            ? WalletVerificationMethod.SignMessage
                                            : WalletVerificationMethod.MicroTransfer,
                                    nonce: challenge.nonce,
                                    verificationSignature: proof.signature,
                                    ...(proof.signedMessage
                                        ? {
                                              verificationSignedMessage:
                                                  proof.signedMessage
                                          }
                                        : {}),
                                    ...(signerAddress ? { signerAddress } : {})
                                }
                            });
                        } catch (error) {
                            // Back to Confirm, the failure on its button.
                            setMultisigGateError(
                                positionSubmitErrorCopy(error)
                            );
                            setPendingMutation(null);
                            throw error;
                        }

                        /* Destination updated — the verification success panel
                         * surfaces the confirmation copy. The address-book save
                         * below is secondary and must not flip this to an error
                         * if it fails. */

                        /* "Save this for later" — write the new address to the
                         * org's address book (Settings → Saved destinations). A
                         * 409 means it's already saved, which is success here, so
                         * only non-conflict failures are worth a warning. */
                        const addressBookEntry =
                            addressBookEntryFromChoice(choice);
                        if (addressBookEntry) {
                            try {
                                await createDestination.mutateAsync(
                                    addressBookEntry
                                );
                            } catch (err) {
                                if (
                                    !(
                                        err instanceof OrgApiError &&
                                        err.status === 409
                                    )
                                ) {
                                    console.warn(
                                        "Rewards destination updated, but saving it to the address book failed.",
                                        err
                                    );
                                }
                            }
                        }
                        onDestinationSaved?.();
                    }
                });
            } catch (error) {
                if (activeEdit.current !== pendingMutation) return;
                setMultisigGateError(positionSubmitErrorCopy(error));
                setPendingMutation(null);
            }
        })();
    }, [
        pendingMutation,
        needsSourceWallet,
        holding,
        onDestinationSaved,
        walletAddress,
        isMultisigPosition,
        multisigStatus,
        position,
        verification,
        editDestination,
        createDestination
    ]);

    /* ── Cancel / close handlers ─────────────────────────── */

    const cancelSubFlow = useCallback(() => {
        setActiveAction(null);
        setPendingMutation(null);
        verification.cancel();
        setShowResubmitConfirm(false);
        setMultisigGateError(null);
    }, [verification]);

    /* Going view-only (e.g. resizing a desktop window below `md` while an
     * edit-rewards / resubmit sub-flow is open) tears down the active
     * mutation panel. `ActionsRow` already disables the entry points on
     * mobile, so this only matters for the resize transition — but it
     * keeps the editor strictly read-only there, including the inner
     * EditRewardsPanel save and the verification flow. */
    useEffect(() => {
        if (viewOnly && (activeAction || pendingMutation)) cancelSubFlow();
    }, [viewOnly, activeAction, pendingMutation, cancelSubFlow]);

    /* ── Edit-rewards save ───────────────────────────────── */

    const handleSaveRewards = useCallback(
        (choice: RewardsDestinationChoice) => {
            setMultisigGateError(null);

            // Saved address-book entries do not prove ownership of this wallet.
            setPendingMutation({
                kind: "edit-rewards",
                payload: { choice }
            });
        },
        []
    );

    const requestSolomonReview = async (): Promise<void> => {
        if (!holding || pendingMutation?.kind !== "edit-rewards") return;
        try {
            await editDestination.mutateAsync({
                positionUuid: holding.positionUuids[0]!,
                data: {
                    programPositionUuids: [...holding.positionUuids].sort(),
                    rewardsDestinationAddress:
                        pendingMutation.payload.choice.address,
                    setWalletDefault: holding.positions.every(
                        (row) => !row.lpPosition
                    ),
                    verificationMode: "direct-solomon",
                    manualReviewDescription:
                        "No wallet approver is available. Please review this reward destination."
                }
            });
            setResubmitFeedback({
                kind: "success",
                title: "Destination review requested",
                description:
                    "The destination will change after Solomon approval. Any payout hold stays active."
            });
            cancelSubFlow();
        } catch (error) {
            setMultisigGateError(
                error instanceof Error
                    ? error.message
                    : "Could not request destination review."
            );
        }
    };

    /* ── Disconnect + re-open wallet picker ──────────────── */

    const handleSwitchWallet = useCallback((): void => {
        setMultisigGateError(null);
        void disconnect().then(() => {
            window.setTimeout(openModal, 0);
        });
    }, [disconnect, openModal]);

    /* ── Resubmit confirm ───────────────────────────────── */

    const handleConfirmResubmit = useCallback(async () => {
        if (!holding) return;
        setShowResubmitConfirm(false);
        // Resubmit every Rejected / Suspended record in the holding — the
        // client sees one position, so one action covers all of them.
        const resubmittable = holding.positions.filter(
            (p) =>
                p.status === YaasPositionStatus.Rejected ||
                p.status === YaasPositionStatus.Suspended
        );
        try {
            for (const p of resubmittable) {
                await resubmit.mutateAsync(p.uuid);
            }
            setResubmitFeedback({
                kind: "success",
                title: "Position resubmitted for review",
                description:
                    "Your distributor will be notified. You'll receive an update when the review completes."
            });
        } catch (error) {
            setResubmitFeedback({
                kind: "error",
                title: "Failed to resubmit position",
                description: positionSubmitErrorCopy(error)
            });
        }
        setActiveAction(null);
    }, [holding, resubmit]);

    if (view === "routing") {
        if (!holding || !position)
            return <Text variant="small">Position unavailable.</Text>;
        if (viewOnly)
            return (
                <Text variant="small">
                    Reward routing is read-only in this view.
                </Text>
            );
        return (
            <div className="flex flex-col gap-4">
                <Text variant="body">
                    This change applies to this holding. For wallet holdings,
                    new program positions will use this destination too. Past
                    rewards keep their original destination.
                </Text>
                {multisigGateError && (
                    <Alert variant="destructive">
                        <AlertDescription>{multisigGateError}</AlertDescription>
                    </Alert>
                )}
                {!pendingMutation && (
                    <RewardsDestinationSection
                        allowUnchanged={
                            view === "routing" &&
                            holding.positions.every((row) => !row.lpPosition)
                        }
                        variant="single"
                        position={{
                            ...position,
                            rewardsDestinationAddress:
                                holding.rewardsDestinationAddress
                        }}
                        status={holding.status}
                        editing
                        onEdit={() => undefined}
                        onConfirm={handleSaveRewards}
                        onDiscard={onRoutingCancel ?? cancelSubFlow}
                    />
                )}
                {pendingMutation && needsSourceWallet && (
                    <SourceWalletGate
                        sourceAddress={position.account.address}
                        connectedAddress={walletAddress}
                        eoaMismatch={eoaWalletMismatch}
                        isMultisig={isMultisigPosition}
                        connecting={connected && !isWalletReady}
                        onConnect={openModal}
                        onSwitchWallet={handleSwitchWallet}
                        onCancel={cancelSubFlow}
                    />
                )}
                {pendingMutation &&
                    !needsSourceWallet &&
                    !verification.isActive && (
                        <Text variant="small">Preparing verification…</Text>
                    )}
                {verification.isActive && (
                    <AccountOwnershipVerificationPanel
                        step={verification.step!}
                        verificationMethod={verification.verificationMethod}
                        error={verification.error}
                        errorKind={verification.errorKind}
                        messageToSign={verification.messageToSign}
                        copy={EDIT_REWARDS_COPY}
                        successCtaLabel="Done"
                        onRetry={verification.retry}
                        onTryMicroTransfer={verification.tryMicroTransfer}
                        onConfirmMicroTransfer={
                            verification.confirmMicroTransfer
                        }
                        onCancelMicroTransfer={
                            verification.cancelCostDisclosure
                        }
                        onContinue={cancelSubFlow}
                        onCancelWaiting={cancelSubFlow}
                    />
                )}
            </div>
        );
    }

    /* ── Render ──────────────────────────────────────────── */

    return (
        <motion.div
            key={positionUuid}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-6"
        >
            <div className="flex flex-col px-4 md:px-6">
                {backLink}
                <header className="flex items-center justify-between md:justify-start gap-8 mt-2 md:mt-3">
                    <Text
                        variant="h2"
                        render={<h2 />}
                        className="text-foreground-base"
                    >
                        {isLoading ? "Loading…" : positionTitle}
                    </Text>
                    {holding && position && (
                        <StatusBlock
                            status={holding.status}
                            rejectionReason={position.rejectionReason}
                            rejectedAt={position.rejectedAt}
                            suspensionReason={position.suspensionReason}
                            suspendedAt={position.suspendedAt}
                        />
                    )}
                </header>
                {holding && (
                    // Vault PDA for Squads multisig, account address for EOA
                    // (matches Squads' own UI). Full while it fits, then
                    // shortened in the middle rather than wrapped.
                    <Address
                        value={holding.address}
                        textVariant="small"
                        mono={false}
                        className="text-foreground-muted mt-0 md:mt-2"
                    />
                )}
            </div>

            {isLoading && (
                <div className="flex flex-col gap-4 px-4 md:px-6">
                    <Skeleton className="h-24 rounded-none border border-line-base" />
                    <Skeleton className="h-24 rounded-none border border-line-base" />
                    <Skeleton className="h-24 rounded-none border border-line-base" />
                </div>
            )}

            {!isLoading && notFound && (
                <Card
                    asElement="section"
                    rounded={false}
                    aria-label="Not found"
                    className="flex flex-col gap-4 p-6 mx-6"
                >
                    <Heading
                        title="Position not found."
                        titleVariant="h2"
                        subtitle={
                            <>
                                We couldn&rsquo;t find a position with that
                                identifier.
                            </>
                        }
                        subtitleVariant="body"
                    />
                </Card>
            )}

            {loadFailed && (
                <Card
                    asElement="section"
                    rounded={false}
                    aria-label="Load failed"
                    className="flex flex-col gap-4 p-6 mx-6"
                >
                    <Heading
                        title="Couldn't load this position."
                        titleVariant="h2"
                        subtitle={
                            <>
                                Something went wrong fetching your positions.
                                Refresh the page to try again.
                            </>
                        }
                        subtitleVariant="body"
                    />
                </Card>
            )}

            {!isLoading && position && awaitingManualReview && (
                <ManualVerificationPending issuerName={issuerName} />
            )}

            {!isLoading && holding && position && !awaitingManualReview && (
                <>
                    {position.lpPosition && (
                        <div className="flex flex-col gap-1 px-4 py-4 md:px-6 border-b border-line-base">
                            <Text
                                variant="small"
                                className="text-foreground-muted break-all"
                            >
                                Pool: {position.lpPosition.poolAddress}
                            </Text>
                            <Text
                                variant="small"
                                className="text-foreground-muted break-all"
                            >
                                {/* The pool is the holding: one LP
                                    position is named, several counted. */}
                                {lpPositionAddresses.length === 1
                                    ? `LP position: ${lpPositionAddresses[0]}`
                                    : `LP positions: ${lpPositionAddresses.length}`}
                            </Text>
                        </div>
                    )}
                    <PositionKeyStats holding={holding} />

                    {/* The holding's related sections as tabs, as a Program's
                        builder on the Issuer Console: Transactions first. */}
                    <div className="relative pt-6">
                        <TabsMenu
                            value={detailTab}
                            onValueChange={(v) =>
                                setDetailTab(v as PositionDetailTab)
                            }
                            align="start"
                            border="bottom"
                            fullWidth
                            className="px-6"
                        >
                            <TabTrigger value="transactions">
                                Transactions
                            </TabTrigger>
                            <TabTrigger value="rewards">
                                Rewards Routing
                            </TabTrigger>
                        </TabsMenu>
                    </div>

                    {detailTab === "rewards" && (
                        <RewardsDestinationSection
                            showTitle={false}
                            mixedDestinations={
                                holdingDestinationAddresses(holding.positions)
                                    .length > 1
                            }
                            variant={rewardsVariant}
                            position={{
                                ...position,
                                // The common destination of the holding's positions.
                                rewardsDestinationAddress:
                                    holding.rewardsDestinationAddress
                            }}
                            status={holding.status}
                            activeAction={activeAction ?? undefined}
                            viewOnly={viewOnly}
                            onEdit={() => {
                                setMultisigGateError(null);
                                handleAction("edit-rewards");
                            }}
                            owner={{
                                label: holding.name,
                                address: holding.address
                            }}
                            editing={activeAction === "edit-rewards"}
                            onConfirm={handleSaveRewards}
                            onDiscard={cancelSubFlow}
                            signing={pendingMutation !== null}
                            signError={multisigGateError}
                            authorizeSlot={
                                /* The signed flow, in place of Verify
                                 * Ownership / Cancel once it is pressed: the
                                 * source-wallet gate until
                                 * that wallet is connected, then the
                                 * verification panel. Its commit runs once,
                                 * from the verification flow. */
                                verification.isActive ? (
                                    <AccountOwnershipVerificationPanel
                                        step={verification.step!}
                                        verificationMethod={
                                            verification.verificationMethod
                                        }
                                        error={verification.error}
                                        errorKind={verification.errorKind}
                                        messageToSign={
                                            verification.messageToSign
                                        }
                                        copy={EDIT_REWARDS_COPY}
                                        successCtaLabel="Done"
                                        onRetry={verification.retry}
                                        onTryMicroTransfer={
                                            verification.tryMicroTransfer
                                        }
                                        onConfirmMicroTransfer={
                                            verification.confirmMicroTransfer
                                        }
                                        onCancelMicroTransfer={
                                            verification.cancelCostDisclosure
                                        }
                                        onContinue={cancelSubFlow}
                                        onCancelWaiting={cancelSubFlow}
                                    />
                                ) : (
                                    <div className="flex flex-col items-start gap-4">
                                        {needsSourceWallet ? (
                                            <SourceWalletGate
                                                sourceAddress={
                                                    position.account.address
                                                }
                                                connectedAddress={walletAddress}
                                                eoaMismatch={eoaWalletMismatch}
                                                isMultisig={isMultisigPosition}
                                                connecting={
                                                    connected && !isWalletReady
                                                }
                                                onConnect={openModal}
                                                onSwitchWallet={
                                                    handleSwitchWallet
                                                }
                                                onCancel={() =>
                                                    setPendingMutation(null)
                                                }
                                            />
                                        ) : (
                                            <Text
                                                variant="small"
                                                className="text-foreground-muted"
                                            >
                                                Preparing verification…
                                            </Text>
                                        )}
                                        {/* No wallet to sign with: Solomon
                                         * reviews the change instead. A
                                         * failed request lives in the
                                         * link, its reason on hover. */}
                                        <Button
                                            variant="link"
                                            disabled={editDestination.isPending}
                                            title={
                                                multisigGateError ?? undefined
                                            }
                                            onClick={() =>
                                                void requestSolomonReview()
                                            }
                                        >
                                            {multisigGateError
                                                ? "Not Sent, Retry"
                                                : "Verify with Solomon"}
                                        </Button>
                                    </div>
                                )
                            }
                        />
                    )}

                    {resubmitFeedback && (
                        <Alert
                            variant={
                                resubmitFeedback.kind === "success"
                                    ? "default"
                                    : "destructive"
                            }
                            className="mx-6 max-w-[720px]"
                        >
                            <AlertTitle>{resubmitFeedback.title}</AlertTitle>
                            <AlertDescription>
                                {resubmitFeedback.description}
                            </AlertDescription>
                        </Alert>
                    )}

                    {multisigGateError && activeAction !== "edit-rewards" && (
                        <Alert
                            variant="destructive"
                            className="mx-6 max-w-[720px]"
                        >
                            <AlertDescription>
                                {multisigGateError}
                            </AlertDescription>
                        </Alert>
                    )}

                    <ActionsRow
                        status={holding.status}
                        onAction={handleAction}
                        activeAction={activeAction ?? undefined}
                        viewOnly={viewOnly}
                    />

                    {detailTab === "transactions" && (
                        <PositionTransactionsCard
                            holding={holding}
                            showTitle={false}
                        />
                    )}
                </>
            )}

            {/* Resubmit confirmation — the only pop-up in this surface */}
            <ConfirmModal
                open={showResubmitConfirm}
                onOpenChange={(o) => {
                    if (!o) cancelSubFlow();
                }}
                titleVariant="h2"
                title="Resubmit this position for review?"
                description="The position will be sent back to your distributor for review. You'll be notified when it's approved."
                confirmLabel={resubmit.isPending ? "Resubmitting…" : "Resubmit"}
                confirmDisabled={resubmit.isPending}
                onConfirm={() => void handleConfirmResubmit()}
            />
        </motion.div>
    );
}

/* ── Source-wallet connect gate ──────────────────────────── */

/**
 * Inline gate shown in place of the EditRewardsPanel when the wallet
 * needed to sign the destination change isn't connected yet:
 *
 *   - No wallet at all: prompt to connect.
 *   - EOA position, wrong wallet connected: prompt to switch.
 *   - Multisig position: any connected wallet renders the panel; the
 *     Vote-permission check runs at Save time.
 */
interface SourceWalletGateProps {
    sourceAddress: string;
    connectedAddress: string | null;
    eoaMismatch: boolean;
    isMultisig: boolean;
    connecting: boolean;
    onConnect: () => void;
    onSwitchWallet: () => void;
    onCancel: () => void;
}

function SourceWalletGate({
    sourceAddress,
    connectedAddress,
    eoaMismatch,
    isMultisig,
    connecting,
    onConnect,
    onSwitchWallet,
    onCancel
}: SourceWalletGateProps): React.ReactElement {
    const eyebrowState = connecting ? "connecting" : "disconnected";
    const eyebrowText = connecting
        ? "Connecting wallet"
        : eoaMismatch
          ? "Wrong wallet connected"
          : "Not connected";

    let bodyText: React.ReactNode;
    if (eoaMismatch && connectedAddress) {
        bodyText = (
            <>
                You&rsquo;re connected with{" "}
                <Text variant="number" render={<span />}>
                    {truncateAddress(connectedAddress)}
                </Text>{" "}
                but this position is held by{" "}
                <Text variant="number" render={<span />}>
                    {truncateAddress(sourceAddress)}
                </Text>
                . Switch to the source wallet to sign the destination change.
            </>
        );
    } else if (isMultisig) {
        bodyText = (
            <>
                Connect a Vote-permission member of multisig{" "}
                <Text variant="number" render={<span />}>
                    {truncateAddress(sourceAddress)}
                </Text>{" "}
                to sign the destination change.
            </>
        );
    } else {
        bodyText = (
            <>
                Connect{" "}
                <Text variant="number" render={<span />}>
                    {truncateAddress(sourceAddress)}
                </Text>{" "}
                to sign the destination change. Past rewards aren&rsquo;t
                affected — only the next distribution cycle forward.
            </>
        );
    }

    const primaryLabel = eoaMismatch
        ? "Switch wallet"
        : connecting
          ? "Connecting…"
          : "Connect wallet";
    const onPrimary = eoaMismatch ? onSwitchWallet : onConnect;

    return (
        <motion.section
            aria-label="Connect source wallet"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-5 p-5 bg-surface-raised border border-line-base rounded-none max-w-[720px]"
        >
            <header className="flex flex-col gap-1.5">
                <Text variant="label" className="text-foreground-muted">
                    Edit rewards destination
                </Text>
                <Text
                    variant="h2"
                    render={<h2 />}
                    className="text-foreground-base"
                >
                    Connect the source wallet to continue.
                </Text>
            </header>

            <div className="flex flex-col gap-1.5">
                <Text
                    variant="small"
                    render={<span />}
                    className="inline-flex items-center gap-1.5 text-foreground-muted"
                >
                    <IndicationUnit
                        variant="connect"
                        state={eyebrowState}
                        size={8}
                    />
                    {eyebrowText}
                </Text>
                <Text
                    variant="body"
                    className="text-foreground-base max-w-[600px]"
                >
                    {bodyText}
                </Text>
            </div>

            <FormActions
                className="pt-1"
                size="sm"
                primaryLabel={primaryLabel}
                onPrimary={onPrimary}
                primaryDisabled={connecting}
                cancelLabel="Cancel"
                onCancel={onCancel}
            />
        </motion.section>
    );
}
